import 'core-js/es/array/includes';
import 'core-js/es/string/trim-right';

// 調剤レセプトのレコード識別情報
//
// オンライン又は光ディスク等による請求に係る記録条件仕様（調剤用） 
// p.2-4 『ウ レコード形式』より
const dispenseIdentifiers = ["YK", "RE", "HO", "KO", "KH", "SN", "JD", "MF", "SH", "CZ", "IY", "TO", "CO", "TK", "KI", "ST", "GO"];
// 返戻レセプトデータ識別コード
//
// オンラインによる一次請求返戻ファイル及び再審査等返戻ファイル並びに再請求ファイルに係る記録条件仕様（調剤用）
// p.14 「第２章 一次請求返戻ファイルに係る記録条件仕様」『４ 情報表記仕様』
// p.38 「第３章 再審査等返戻ファイルに係る記録条件仕様」『４ 情報表記仕様』より
const returnReceiptCodes = ["1", "2", "3", "4", "5", "6", "8", "11", "12", "13", "14", "15", "16", "85", "86"];
// 返戻レセプトのレコード識別情報
//
// オンラインによる一次請求返戻ファイル及び再審査等返戻ファイル並びに再請求ファイルに係る記録条件仕様（調剤用）
// p.48-51 「第４章 再請求ファイルに係る記録条件仕様」『ウ レコード形式』より
const returnIdentifiers = ["HR", "RC", "MN", "JY", "EX", "MD", "RT", "JR", "MK", "ON"].concat(dispenseIdentifiers);

export type CheckStatus =
  "ok"
  | "E01"   // 先頭行が /^YK,/ 以外
  | "E02"   // 終端行が /^GO,/ 以外
  | "E11"   // 中身に不正なコードがある (返戻レセプト以外)
  | "E12"   // 中身に不正なコードがある (返戻レセプト)
  | "E21"   // YK レコードの審査支払機関が 1,2 以外
  | "E31"   // 匿名化処理済と未処理のレコードが混在している
  | "E99";  // その他ファイルエラー (改行コード違い etc.)
export const ErrorStatusReasons = {
  "E01": "先頭行が薬局情報レコード(YK)ではありません。",
  "E02": "終端行が調剤報酬請求書レコード(GO)ではありません。",
  "E11": "ファイルの中に調剤レセプトとして不明なレコードが存在します。",
  "E12": "ファイルの中に返戻レセプトとして不明なレコードが存在します。",
  "E21": "薬局情報レコード内に不明な審査支払機関コードが記録されています。",
  "E31": "ファイルの中に正常に匿名化処理されているレコードとされていないレコードが混在しています。",
  "E99": "不正な調剤レセプトファイルです。"
}
export interface ValidCYOStatus {
  status: CheckStatus
  errorLineNo?: number
  errorColumnNo?: number
  errorVal?: string
  isHashed?: boolean
}

export function checkValidCYO(fileContents: string): ValidCYOStatus {
  const eof = "\u001a"; // \u001a = EOF レコード
  let status: CheckStatus = 'ok';
  let lineno = 1;
  let columnNo = 1;
  let errorVal = '';
  let lastLine = '';
  let isHashed: boolean|undefined;

  // 改行(CRLF)が存在しない時点で不正なファイルなので E99 エラーを返す
  if (fileContents.indexOf("\r\n") === -1) {
    return { status: "E99" };
  }

  const lines = fileContents.split(/\r\n/);
  lines.every((line, i) => {
    // 先頭行が YK でなければ E01 エラーを返す
    if (i === 0 && !/^YK,/.test(line)) {
      status = "E01";
      lineno = 1;
      columnNo = 1;
      errorVal = line.split(/,/)[0].substring(0,2);
      return false;
    }
    // 空行か EOF レコードのみの場合はチェックを飛ばす
    if (line === '' || line === eof) {
      return true;
    }
    const records = line.split(/,/, 5);
    // 調剤レセプトのレコード識別情報でない場合
    if (!dispenseIdentifiers.includes(records[0])) {
      // 返戻レセプトでもない場合 E11 エラーを返す
      if (!returnReceiptCodes.includes(records[0])) {
        status = "E11";
        lineno = i + 1;
        columnNo = 1;
        errorVal = records[0].substring(0,2);
        return false;
      }
      // 返戻レセプトだけれどレコード識別情報が未知の場合 E12 エラーを返す
      if (!returnIdentifiers.includes(records[3])) {
        status = "E12";
        lineno = i + 1;
        columnNo = 4;
        errorVal = records[3].substring(0,2);
        return false;
      }
    }
    // YK レコードの審査支払期間コードが1,2以外の場合は E21 エラーを返す
    if (records[0] === 'YK' && !["1", "2"].includes(records[1])) {
      status = "E21";
      lineno = i + 1;
      columnNo = 2;
      errorVal = records[1].substring(0,1);
      return false;
    }
    // RE レコードの5列目がハッシュ化されているかどうかを検査
    if (records[0] === 'RE' && records[4] !== '') {
      const testHashed = /^[0-9a-f]{40}$/.test(records[4]);
      // 未チェックならばまずチェックした値を入れる
      if (isHashed === undefined) {
        isHashed = testHashed;
      // チェック済で既存のチェック値と違う場合にはエラーを返す
      } else if (isHashed !== testHashed) {
        status = "E31";
        lineno = i + 1;
        columnNo = 5;
        return false;
      }
    }
    lastLine = line;
    return true;
  });
  if (status !== "ok") {
    return { status, errorLineNo: lineno, errorColumnNo: columnNo, errorVal: errorVal };
  }
  if (!/^GO,/.test(lastLine)) {
    // 終端行が GO レコードでなければ E02 エラーを返す
    return { status: "E02", errorLineNo: lines.length };
  }
  return { status: "ok", isHashed };
}
